<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { CtaBaseProps } from "@/views/blocks/cta/cta.interfaces";
import DescriptionComponent from "@/views/blocks/cta/shared/description/DescriptionComponent.vue";
import PrimaryButton from "@/views/blocks/cta/shared/primary-button/PrimaryButton.vue";
import SecondaryButton from "@/views/blocks/cta/shared/secondary-button/SecondaryButton.vue";
import TagComponent from "@/views/blocks/cta/shared/tag/TagComponent.vue";
import TitleComponent from "@/views/blocks/cta/shared/title/TitleComponent.vue";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import { computed } from "vue";

const props = defineProps<{
    block: PageBlock<CtaBaseProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <div :style="{ backgroundColor: styleValues.background }" class="py-24 sm:py-32">
        <div
            :id="block.webId"
            class="mx-6 xl:mx-auto max-w-7xl md:pl-16 md:pt-16 pl-8 pt-8 rounded-3xl flex flex-wrap shadow-sm gradient-background"
        >
            <div class="md:w-1/2 flex flex-col justify-center md:pb-16 md:pr-16 pb-8 pr-8">
                <EditableElement
                    v-if="!dataValues.tag.isHidden"
                    :blockId="block.id"
                    field-name="tag"
                >
                    <TagComponent :text="dataValues.tag.data.text" :textColor="styleValues.brand" />
                </EditableElement>
                <EditableElement
                    v-if="!dataValues.title.isHidden"
                    :blockId="block.id"
                    field-name="title"
                >
                    <TitleComponent
                        :text="dataValues.title.data.text"
                        :textColor="styleValues.text"
                        :tag="dataValues.title.data.tag"
                    />
                </EditableElement>
                <EditableElement
                    v-if="!dataValues.description.isHidden"
                    :blockId="block.id"
                    field-name="description"
                >
                    <DescriptionComponent
                        :text="dataValues.description.data.text"
                        :textColor="styleValues.text"
                    />
                </EditableElement>
                <div class="mt-8 flex gap-x-6 items-center lg:justify-start">
                    <EditableElement
                        v-if="!dataValues.primaryButton.isHidden"
                        :blockId="block.id"
                        field-name="primaryButton"
                    >
                        <PrimaryButton
                            v-bind="dataValues.primaryButton.data"
                            :textColor="styleValues.background"
                            :backgroundColor="styleValues.brand"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.secondaryButton.isHidden"
                        :blockId="block.id"
                        field-name="secondaryButton"
                    >
                        <SecondaryButton
                            v-bind="dataValues.secondaryButton.data"
                            :textColor="styleValues.brand"
                        />
                    </EditableElement>
                </div>
            </div>
            <EditableElement
                v-if="!dataValues.image.isHidden"
                :blockId="block.id"
                field-name="image"
            >
                <img
                    class="ml-auto md:w-1/2 object-cover object-left"
                    :src="dataValues.image.data.src"
                    :alt="dataValues.image.data.alt"
                />
            </EditableElement>
        </div>
    </div>
</template>

<style scoped>
.gradient-background {
    background: linear-gradient(
        to right,
        color-mix(in srgb, transparent 95%, v-bind("styleValues.brand")),
        color-mix(in srgb, transparent 80%, v-bind("styleValues.brand"))
    );
}
</style>
